import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, ArrowLeft, Check, Minus, Plus, Lock, Truck, ChevronDown } from "lucide-react";
import { fetchProduct, money, img } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import ManukaEducation from "@/components/ManukaEducation";
import ReviewsSection from "@/components/ReviewsSection";
import { useCatalog } from "@/hooks/useCatalog";
import { PRODUCT_INFO } from "@/data/content";

function ingredientsFor(product) {
  const n = (product.name || "").toLowerCase();
  if (n.includes("100% organic golden jojoba")) return "100% organic golden jojoba oil. Single ingredient, unscented.";
  if (n.includes("saffron") && n.includes("jojoba")) return "Organic golden jojoba oil infused with saffron and rose.";
  return null; // Hide when we can't state ingredients accurately.
}

function Accordion({ title, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-wood-900/10">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={testid}
        className="flex w-full items-center justify-between py-5 text-left"
      >
        <span className="font-serif text-xl text-wood-900">{title}</span>
        <ChevronDown size={20} className={`text-honey-dark transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="pb-6 text-base leading-relaxed text-wood-700">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem, setOpen } = useCart();
  const { products } = useCatalog();
  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => fetchProduct(slug),
  });

  const [variationId, setVariationId] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (product) {
      setVariationId(product.variations[0]?.id);
      setActiveImg(0);
      setQty(1);
      document.title = `${product.name} · Washington Family Provisions`;
      const desc = (product.description || "").slice(0, 155);
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = desc;
      // Preload primary image (WebP via proxy) to remove blank areas.
      const href = img(product.images[0], 1100);
      let l = document.getElementById("pdp-preload");
      if (!l) { l = document.createElement("link"); l.id = "pdp-preload"; l.rel = "preload"; l.as = "image"; document.head.appendChild(l); }
      l.href = href;
    }
  }, [product]);

  const variation = useMemo(
    () => product?.variations.find((v) => v.id === variationId),
    [product, variationId]
  );

  const related = useMemo(() => {
    if (!product) return [];
    return products
      .filter((p) => p.slug !== product.slug && p.categories.some((c) => product.categories.includes(c)))
      .slice(0, 4);
  }, [product, products]);

  if (isLoading) return <div className="flex justify-center py-40"><Loader2 className="animate-spin text-honey-dark" /></div>;
  if (isError || !product) return <div className="py-40 text-center font-serif text-3xl text-wood-900">Product not found.</div>;

  const inStock = variation ? variation.in_stock !== false : product.in_stock !== false;
  const info = PRODUCT_INFO[product.categories[0]] || PRODUCT_INFO.honey;
  const ingredients = ingredientsFor(product);

  const add = () => {
    if (!inStock) return;
    addItem({
      variationId: variation.id,
      productId: product.id,
      slug: product.slug,
      name: product.name,
      variationName: variation.name,
      price: variation.price,
      image: product.images[0],
      qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: product.images,
    brand: { "@type": "Brand", name: "Washington Family Provisions" },
    offers: product.variations.map((v) => ({
      "@type": "Offer",
      sku: v.sku,
      price: v.price,
      priceCurrency: v.currency,
      availability: v.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
    })),
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 md:px-8 md:py-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Link to={`/shop/${product.categories[0]}`} className="mb-6 inline-flex items-center gap-2 text-base text-wood-500 hover:text-honey-dark">
        <ArrowLeft size={16} /> Back to shop
      </Link>

      <div className="grid gap-10 md:grid-cols-2 md:gap-16">
        <div>
          <div className="relative overflow-hidden rounded-3xl bg-sand">
            {!imgLoaded && <div className="skeleton absolute inset-0" aria-hidden="true" />}
            <motion.img
              key={activeImg}
              src={img(product.images[activeImg], 1100)}
              alt={product.name}
              onLoad={() => setImgLoaded(true)}
              initial={{ opacity: 0 }}
              animate={{ opacity: imgLoaded ? 1 : 0 }}
              className="aspect-square w-full object-cover"
              data-testid="product-main-image"
            />
          </div>
          {product.images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {product.images.map((image, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveImg(i); setImgLoaded(true); }}
                  aria-label={`View image ${i + 1}`}
                  className={`h-20 w-20 overflow-hidden rounded-xl border-2 ${activeImg === i ? "border-honey-dark" : "border-transparent"}`}
                >
                  <img src={img(image, 160)} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:pt-4">
          {product.is_manuka && (
            <span className="inline-block rounded-full bg-wood-900 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-cream">MGO 234+</span>
          )}
          <h1 className="mt-4 font-serif text-4xl leading-tight text-wood-900 md:text-6xl">{product.name}</h1>
          <p className="mt-4 font-serif text-4xl text-honey-dark" data-testid="product-price">{money(variation?.price)}</p>

          <div className="mt-3 flex items-center gap-2 text-base" data-testid="stock-status">
            <span className={`h-2.5 w-2.5 rounded-full ${inStock ? "bg-sage" : "bg-rose"}`} />
            <span className={inStock ? "text-sage" : "text-rose"}>
              {inStock ? "In stock — ready to ship" : "Currently unavailable"}
            </span>
          </div>

          {product.variations.length > 1 && (
            <div className="mt-8">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-wood-500">Size</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {product.variations.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVariationId(v.id)}
                    data-testid={`variation-${v.id}`}
                    className={`rounded-full border px-7 py-3.5 text-base font-medium transition-colors ${
                      variationId === v.id ? "border-wood-900 bg-wood-900 text-cream" : "border-wood-900/25 text-wood-900 hover:border-wood-900"
                    }`}
                  >
                    {v.name || "One size"} — {money(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center rounded-full border border-wood-900/25">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease" aria-label="Decrease quantity" className="px-4 py-3.5"><Minus size={18} /></button>
              <span className="min-w-8 text-center text-lg font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-increase" aria-label="Increase quantity" className="px-4 py-3.5"><Plus size={18} /></button>
            </div>
            <button
              onClick={add}
              disabled={!inStock}
              data-testid="add-to-cart"
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-wood-900 px-12 py-4 text-lg font-medium text-cream transition-colors hover:bg-honey-dark disabled:cursor-not-allowed disabled:opacity-50 md:flex-none"
            >
              {added ? <><Check size={20} /> Added</> : inStock ? "Add to Cart" : "Unavailable"}
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-1.5 text-sm text-wood-500">
            <span className="inline-flex items-center gap-2"><Lock size={15} className="text-sage" /> Secure checkout by Square</span>
            <span className="inline-flex items-center gap-2"><Truck size={15} className="text-sage" /> {info.shipping}</span>
          </div>

          <div className="mt-8">
            {product.description && (
              <Accordion title="Description" testid="acc-description" defaultOpen>
                <p className="whitespace-pre-line">{product.description}</p>
              </Accordion>
            )}
            {ingredients && (
              <Accordion title="Ingredients" testid="acc-ingredients">
                <p>{ingredients}</p>
              </Accordion>
            )}
            <Accordion title="How to Enjoy" testid="acc-directions"><p>{info.directions}</p></Accordion>
            <Accordion title="Storage" testid="acc-storage"><p>{info.storage}</p></Accordion>
            <Accordion title="Shipping & Returns" testid="acc-returns">
              <p>{info.shipping} See our <Link to="/policies/shipping" className="text-honey-dark underline">Shipping</Link> and <Link to="/policies/returns" className="text-honey-dark underline">Returns</Link> policies for full details.</p>
            </Accordion>
          </div>
          <p className="mt-6 text-sm text-wood-500">Prepared in small batches. Natural products may vary slightly in color and texture. Not intended to diagnose, treat, or cure any condition.</p>
        </div>
      </div>

      {product.is_manuka && <ManukaEducation />}

      <ReviewsSection productSlug={product.slug} />

      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-serif text-3xl text-wood-900 md:text-4xl">You may also love</h2>
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4 md:gap-6">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
